import type { Prototype } from '@atlas/api-contract';
import { useMemo } from 'react';
import { toast } from 'sonner';

import { PrototypeFormDialog } from './prototype-form-dialog';
import {
  toUpdatePrototypePayload,
  type PrototypeFormValues,
} from './prototype-schemas';
import { useUpdatePrototype } from './prototypes-queries';

export interface EditPrototypeDialogProps {
  /** The prototype being edited, or `null` when nothing is. */
  prototype: Prototype | null;
  onOpenChange: (open: boolean) => void;
}

/**
 * Rename a prototype, or rewrite what it says about itself.
 *
 * Uploaded versions are immutable and are not touched by any of this: renaming
 * changes the label on the card, never the files somebody already reviewed. The
 * unique-name 409 is inherited from {@link PrototypeFormDialog} rather than
 * restated here.
 */
export const EditPrototypeDialog = ({
  prototype,
  onOpenChange,
}: EditPrototypeDialogProps) => {
  const updatePrototype = useUpdatePrototype();

  const defaultValues = useMemo<PrototypeFormValues>(
    () => ({
      name: prototype?.name ?? '',
      description: prototype?.description ?? '',
      sourceTool: prototype?.sourceTool ?? '',
    }),
    [prototype],
  );

  const handleSubmit = async (values: PrototypeFormValues): Promise<boolean> => {
    if (prototype === null) {
      return false;
    }

    const updated = await updatePrototype.mutateAsync({
      prototypeId: prototype.id,
      projectId: prototype.projectId,
      payload: toUpdatePrototypePayload(values),
    });

    toast.success(`Saved “${updated.name}”.`);
    return true;
  };

  return (
    <PrototypeFormDialog
      open={prototype !== null}
      onOpenChange={onOpenChange}
      title="Edit prototype"
      description="Renaming a prototype does not change the versions already uploaded to it."
      submitLabel="Save changes"
      pendingLabel="Saving…"
      defaultValues={defaultValues}
      onSubmit={handleSubmit}
    />
  );
};
